import { useMemo, useEffect, useState } from "react";
import { selectCurrentProject } from "@features/project/projectSlice";
import { useAppSelector, useThreadsProcessor } from "@app/hooks";
import { Formik, FormikHelpers } from "formik";
import { BEModulesClassNames } from "@utils/moduleList";
import { ModuleFull } from "./packagingTypes";
import { useContextAwareGetPackaging, useUpdatePackagingsMutation, useUpdatePackagingEntryMutation, useContextAwareGetPackagingEntries } from "@hooks/index";
import { getSchemas, validationSchema, initialValues, energyNullData } from "./packaging";
import { FEModules } from "@utils/moduleList";
import { selectCurrentBuilder } from "@features/builder/builderSlice";
import useModuleHook from "../useModuleHook";
import useErrorMessage from "@utils/useErrorMessage";
import PackagingTierOne from "./PackagingTierOne";
import GenericTierTwo from "../GenericTierTwo";

const PackagingModule = () => {
  const { activeActivityId } = useAppSelector(selectCurrentProject);
  const { moduleList } = useAppSelector(selectCurrentBuilder);
  const id = moduleList.find((module) => module.id === FEModules.Packaging)?.uniqueId;

  const { data: packagingsData, isLoading, isError, error } = useContextAwareGetPackaging(id ?? 0, { refetchOnMountOrArgChange: true });
  const { data: entriesData, isLoading: isEntriesLoading, isError: isEntriesError, error: entriesError } = useContextAwareGetPackagingEntries({ activityId: activeActivityId ?? 0 });
  const [updatePackagings, { isError: isUpdateError, error: updateError }] = useUpdatePackagingsMutation();
  const [updatePackagingEntry, { isError: isUpdatePackagingEntryError, error: updatePackagingEntryError, isLoading: isUpdatePackagingEntryLoading }] = useUpdatePackagingEntryMutation();


  const { moduleSchema, t2Schema } = useMemo(() => getSchemas(), []);
  const [initValues, setInitValues] = useState<ModuleFull>(initialValues);
  const { tabsOpen, errorMsg: updateParentErrorMsg } = useModuleHook({
    skeletonsLoadingDeps: [isLoading, isEntriesLoading],
    isMutateError: isUpdateError,
    mutateError: updateError,
  });

  const { errorMsg: fetchError } = useErrorMessage({ isError: isEntriesError ?? isError, error: entriesError ?? error });
  const { errorMsg: updateEntryErrorMsg } = useErrorMessage({ isError: isUpdatePackagingEntryError, error: updatePackagingEntryError });
  const { processModuleThreads } = useThreadsProcessor<ModuleFull>();

  useEffect(() => {
    if (!packagingsData) return
    setInitValues((cur) => ({ ...cur, parent: packagingsData }))
  }, [packagingsData])

  useEffect(() => {
    if (entriesData) processModuleThreads({ packagings: entriesData })
  }, [processModuleThreads, entriesData])

  const handleSubmit = async (
    values: ModuleFull,
    { resetForm }: FormikHelpers<ModuleFull>
  ) => {
    const { packagings, parent } = values
    try {
      const promises: Promise<any>[] = []
      const parentPayload = { body: { ...parent }, activityId: activeActivityId ?? 0 }

      packagings.map((entry) => {
        const payload = { body: entry.is_electric ? { ...entry } : { ...entry, ...energyNullData }, activityId: activeActivityId ?? 0 }
        if (entry.id) promises.push(updatePackagingEntry(payload))
        return Promise.resolve()
      })

      await Promise.all(promises);
      await new Promise(resolve => setTimeout(resolve, 500));
      if (parent.id) updatePackagings(parentPayload)
      resetForm({ values })
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <Formik
      initialValues={initValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      <>
        <PackagingTierOne
          title="project.packaging"
          initValues={initValues}
          t2Schema={t2Schema}
          beModuleType={BEModulesClassNames.Packaging}
          moduleSchema={moduleSchema}
          isLoading={isLoading || isEntriesLoading || isUpdatePackagingEntryLoading}
          isEntriesLoading={isEntriesLoading}
          footerError={updateParentErrorMsg ?? updateEntryErrorMsg}
          blockingError={fetchError}
          setInitValues={setInitValues}
        />
        <GenericTierTwo
          title="project.packaging"
          t2Schema={t2Schema}
          tabsOpen={tabsOpen}
        />
      </>
    </Formik>
  );
}

export default PackagingModule;